import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentMultiDocumentVerificationData,
  type EnrollmentMultiDocumentVerificationSubmitInput,
  type EnrollmentMultiDocumentVerificationSubmitOutput,
  type EnrollmentMultiDocVerificationCheckStatusInput,
} from '../../../../models'

/**
 * Get Enrollment Multi DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationData>
 * @example const response = await getEnrollmentMultiDocumentVerificationData()
 */
export const getEnrollmentMultiDocumentVerificationData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiDocumentVerificationData',
      query: `mutation getEnrollmentMultiDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              documentVerificationStatus
              documentVerificationUrl
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiDocumentVerificationData = createQueryHook<
  undefined,
  EnrollmentMultiDocumentVerificationData
>(getEnrollmentMultiDocumentVerificationData)

/**
 * Submit Enrollment Multi DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentMultiDocumentVerificationData();
 */
export const submitEnrollmentMultiDocumentVerificationData = async (
  enrollmentDocumentVerificationSubmitInput: EnrollmentMultiDocumentVerificationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDocumentVerificationData',
      query: `mutation submitEnrollmentMultiDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationUrl
              documentVerificationStatus
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocumentVerificationSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDocumentVerificationData = createMutationHook<
  EnrollmentMultiDocumentVerificationSubmitInput,
  EnrollmentMultiDocumentVerificationSubmitOutput
>(submitEnrollmentMultiDocumentVerificationData)

/**
 * Submit Enrollment Multi DocumentVerificationCheckStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentMultiDocVerificationCheckStatusData();
 */
export const submitEnrollmentMultiDocVerificationCheckStatusData = async (
  enrollmentDocVerificationCheckStatusInput: EnrollmentMultiDocVerificationCheckStatusInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDocVerificationCheckStatusData',
      query: `mutation submitEnrollmentMultiDocVerificationCheckStatusData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationStatus
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocVerificationCheckStatusInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDocVerificationCheckStatusData = createMutationHook<
  EnrollmentMultiDocVerificationCheckStatusInput,
  EnrollmentMultiDocumentVerificationSubmitOutput
>(submitEnrollmentMultiDocVerificationCheckStatusData)
